import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { Link } from "wouter";
import { 
  Users, 
  Calendar, 
  CheckCircle2, 
  ClipboardList, 
  PieChart,
  BriefcaseIcon,
  Clock,
  Bell
} from "lucide-react";

export default function CaseWorkerDashboard() {
  const { user } = useAuth();

  return (
    <DashboardLayout pageTitle="Case Worker Dashboard">
      {/* Overview Cards */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Active Clients</p>
                <h3 className="text-2xl font-bold mt-1">24</h3>
              </div>
              <div className="p-3 bg-primary-100 rounded-full">
                <Users className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <div className="flex items-center mt-4">
              <CheckCircle2 className="text-green-500 h-4 w-4 mr-1" />
              <span className="text-xs text-slate-500">6 new this month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Appointments Today</p>
                <h3 className="text-2xl font-bold mt-1">5</h3>
              </div>
              <div className="p-3 bg-primary-100 rounded-full">
                <Calendar className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <div className="flex items-center mt-4">
              <Clock className="text-amber-500 h-4 w-4 mr-1" />
              <span className="text-xs text-slate-500">Next: Resume Review at 10AM</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Placements This Month</p>
                <h3 className="text-2xl font-bold mt-1">8</h3>
              </div>
              <div className="p-3 bg-primary-100 rounded-full">
                <BriefcaseIcon className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <div className="flex items-center mt-4">
              <CheckCircle2 className="text-green-500 h-4 w-4 mr-1" />
              <span className="text-xs text-slate-500">Up 33% from last month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Action Items</p>
                <h3 className="text-2xl font-bold mt-1">12</h3>
              </div>
              <div className="p-3 bg-primary-100 rounded-full">
                <ClipboardList className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <div className="flex items-center mt-4">
              <Bell className="text-red-500 h-4 w-4 mr-1" />
              <span className="text-xs text-slate-500">4 require immediate attention</span>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Upcoming Appointments */}
      <section className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Today's Appointments</h2>
          <Button asChild variant="outline" size="sm">
            <Link href="/dashboard/case-worker/appointments">View All</Link>
          </Button>
        </div>
        <div className="space-y-4">
          {[
            { time: "10:00 AM", name: "James Wilson", type: "Resume Review", status: "Confirmed" },
            { time: "11:30 AM", name: "Maria Garcia", type: "Job Placement", status: "Confirmed" },
            { time: "1:15 PM", name: "Robert Johnson", type: "Initial Assessment", status: "Confirmed" },
            { time: "3:00 PM", name: "Emily Chen", type: "Weekly Check-in", status: "Tentative" },
            { time: "4:30 PM", name: "David Brown", type: "Housing Support", status: "Confirmed" }
          ].map((appointment, index) => (
            <Card key={index}>
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <div className="bg-primary-100 text-primary-700 p-3 rounded-full mr-4">
                      <Calendar className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-medium">{appointment.name}</p>
                      <p className="text-sm text-slate-500">{appointment.type}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{appointment.time}</p>
                    <p className={`text-sm ${appointment.status === "Confirmed" ? "text-green-600" : "text-amber-600"}`}>
                      {appointment.status}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Recent Clients and Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <section className="lg:col-span-2">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Recent Clients</h2>
            <Button asChild variant="outline" size="sm">
              <Link href="/dashboard/case-worker/clients">View All Clients</Link>
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { name: "James Wilson", status: "Job Searching", progress: "Resume Complete" },
              { name: "Maria Garcia", status: "Interviewing", progress: "3 Interviews Scheduled" },
              { name: "Robert Johnson", status: "Placement", progress: "Started New Job" },
              { name: "Emily Chen", status: "Onboarding", progress: "Paperwork Pending" }
            ].map((client, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <div className="flex justify-between">
                    <div>
                      <p className="font-medium">{client.name}</p>
                      <div className="flex items-center text-sm text-slate-500">
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                          client.status === "Placement" ? "bg-green-500" : 
                          client.status === "Interviewing" ? "bg-blue-500" : 
                          client.status === "Onboarding" ? "bg-purple-500" : 
                          "bg-amber-500"
                        }`}></span>
                        <span>{client.status}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{client.progress}</p>
                    </div>
                    <Button size="sm" variant="ghost">View</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
        
        <section>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Performance</h2>
            <Button variant="ghost" size="sm">
              <PieChart className="h-4 w-4 mr-1" />
              <span>Full Report</span>
            </Button>
          </div>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Client Status Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 flex justify-center">
                {/* Placeholder for chart */}
                <div className="w-36 h-36 rounded-full bg-slate-100 flex items-center justify-center">
                  <span className="text-slate-400 text-xs">Chart Placeholder</span>
                </div>
              </div>
              <div className="space-y-2 mt-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <span className="inline-block w-3 h-3 rounded-full bg-amber-500 mr-2"></span>
                    <span className="text-sm">Job Searching</span>
                  </div>
                  <span className="text-sm font-medium">45%</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
                    <span className="text-sm">Interviewing</span>
                  </div>
                  <span className="text-sm font-medium">20%</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-2"></span>
                    <span className="text-sm">Placement</span>
                  </div>
                  <span className="text-sm font-medium">25%</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <span className="inline-block w-3 h-3 rounded-full bg-purple-500 mr-2"></span>
                    <span className="text-sm">Onboarding</span>
                  </div>
                  <span className="text-sm font-medium">10%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </DashboardLayout>
  );
}